import { Pagination, TableColumnsType, Tooltip, Typography } from "antd";
import { debounce, map } from "lodash";
import moment from "moment/moment";
import { DataTable } from "~/Components";
import { get } from "~/Services";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { getFormattedDate } from "@utils/CommonUtils";

export const ReviewerCommentsView = () => {
  const { id } = useParams();
  const { openToast } = useNotification();
  const [totalRecords, setTotalRecords] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState<any>([]);
  const query = useQuery();
  const [pageStart, setPageStart] = useState<number>(0);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const [page, setpage] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState("desc");
  const [orderByName, setOrderByName] = useState("commentedOn");
  const [searchString, setSearchString] = useState("");
  const columns: TableColumnsType = [
    {
      title: "Designation",
      dataIndex: "reviewerDesignation",
      render: (value: any) => {
        if (!value) {
          return <Typography.Text disabled>-</Typography.Text>;
        } else if (value === "AIRPORT MANAGER") {
          return "Airport Manager";
        }
        return value;
      },
      sorter: (a: any, b: any) => (a.reviewerDesignation || "").localeCompare(b.reviewerDesignation || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "reviewerDesignation"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "reviewerDesignation"
          ? "descend"
          : undefined,
    },
    {
      title: "Name",
      dataIndex: "reviewerName",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.reviewerName || "").localeCompare(b.reviewerName || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "reviewerName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "reviewerName"
          ? "descend"
          : undefined,
    },
    {
      title: "Email Address",
      dataIndex: "commentedBy",
      render: (value: any, record: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.commentedBy || "").localeCompare(b.commentedBy || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "commentedBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "commentedBy"
          ? "descend"
          : undefined,
    },
    {
      title: "Document Name",
      dataIndex: "documentName",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.documentName || "").localeCompare(b.documentName || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "documentName"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "documentName"
          ? "descend"
          : undefined,
    },
    {
      title: "Document Version",
      dataIndex: "versionOrder",
      render: (value: any, record: any) => {
        if (!value) return "-";
        const [major, minor] = value.split(".").map(Number);
        const version = (major - 1) * 10 + minor;
        return `V${version}`;
      },
      // change here
      sorter: (a: any, b: any) => (a.versionOrder || "").localeCompare(b.versionOrder || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "versionOrder"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "versionOrder"
          ? "descend"
          : undefined,
    },
    {
      title: "Comments",
      dataIndex: "message",
      render: (value: any) => {
        if (!value) return <Typography.Text disabled>-</Typography.Text>;
    
        const parser = new DOMParser();
        const doc = parser.parseFromString(value, "text/html");
        Array.from(doc.querySelectorAll("a")).forEach((a) => {
          const link = document.createElement("a");
          link.href = a.href;
          link.target = "_blank";
          link.rel = "noopener noreferrer";
          link.innerHTML = a.innerHTML;
          a.replaceWith(link);
        });
        const formattedHtml = doc?.body?.innerHTML;
        return (
          <Tooltip title={<span dangerouslySetInnerHTML={{ __html: formattedHtml }} />} overlayStyle={{ maxWidth: 400 }}>
        <Typography.Link underline>
          View Comment
        </Typography.Link>
      </Tooltip>
        );
      },
      sorter: (a: any, b: any) => (a.message || "").localeCompare(b.message || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "message"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "message"
          ? "descend"
          : undefined,
          ellipsis: true,
    },    
    {
      title: "Commented At",
      dataIndex: "commentedOn",
      render: (value: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.commentedOn || "").localeCompare(b.commentedOn || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "commentedOn"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "commentedOn"
          ? "descend"
          : undefined,
    },
  ];

  const parseHTML = (htmlString: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    
    const links = Array.from(doc.querySelectorAll("a")).map((a) => ({
      href: a.href,
      text: a.innerText || a.href,
    }));
  
    return links.length > 0 ? links : [{ text: htmlString }];
  };

  const getDocuments = (sortOrder: any, orderByName: any, searchString: any, startIndex: any) => {
    let url = `v1/workflow/getCommentsList?envelopeId=${id}&page=${startIndex}&size=${recordsPerPage}&searchText=${searchString}&order=${sortOrder}&orderBy=${orderByName}`;
    get(url)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          let comments: any[] = [];
          let commentsList = res.response.data.records;
          const extractedData = commentsList.map((record: any) => {
            let parsedMessage = parseHTML(record.message);
            return {
              ...record,
              parsedMessage,
            };
          });
          // commentsList.map((item: any) => {
          //   let obj = {
          //     documentName: item.documentName,
          //     versionOrder: item.versionOrder,
          //     // order: item.order,
          //   };
          //   item.comments.map((comment: any) => {
          //     comments.push({
          //       ...obj,
          //       ...comment,
          //     });
          //   });
          // });
          setTotalRecords(extractedData);
          setTotal(res.response.data.totalCount);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        // openToast({
        //   content: err,
        //   type: "error",
        // });
        setIsLoading(false);
      });
  };
  useEffect(() => {
    getDocuments(sortOrder, orderByName, searchString, searchString.length > 0 ? 0 : pageStart);
  }, [searchString, pageStart]);

  const handleTableSort = (column: any, sortOrder: string, page: any) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    getDocuments(order, column.dataIndex, searchString, pageStart);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
  }, 1000);

  return (
    <>
      <DataTable
        columns={columns?.map((column) => ({
          ...column,
          onHeaderCell: (column: any) => ({
            onClick: () => {
              if (column.sorter) {
                handleTableSort(column, sortOrder, page ? page : 0);
              }
            },
          }),
        }))}
        height={400}
        multiSelect={false}
        tableData={totalRecords}
        onSearchChange={handleSearchChange}
        searchPlaceholderText={"Search"}
        showTopBar={false}
        recordsPerPage={recordsPerPage}
        hideExport={true}
        isLoading={isLoading}
        isPagination={true}
        isServerSearchApi={true}
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          total={total}
          current={page + 1}
          onChange={(page) => {
            const pageSize = 10;
            const startIndex = page - 1;
            setpage(page - 1);
            setPageStart(startIndex);
            getDocuments(sortOrder, orderByName, searchString, startIndex);
          }}
          showSizeChanger={false}
        />
      </div>
    </>
  );
};
